import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuthContext } from 'AuthProvider'
import { type CellValueChangedEvent } from 'ag-grid-community'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay } from 'components/commonparts/index'
import { useGridEdit } from 'hooks/useGridEdit'
import { useSupplier } from 'hooks/useSupplier';
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes'
import { validateRequired } from 'utils/validation'

import type { ColDef, ColGroupDef, ValueFormatterParams, ValueParserParams, IRichCellEditorParams } from 'ag-grid-enterprise'
import type { Supplier, MstSupplier, GetMstSuppliersSupplierGetRequest, Codes, Groups, MstCenter } from 'api-client';
import 'utils/mst.scss'

const columnMapping: Record<string, string> = {
    "org_supplier_id": "orgSupplierId",
    "supplier_name": "supplierName",
    "supplier_hojin_no" : "supplierHojinNo",
    "supplier_address" : "supplierAddress",
    "supplier_gln" : "supplierGln",
    "transport_org_center_id": "transportOrgCenterId",
    "min_order_quantity_once" : "minOrderQuantityOnce",
    "order_leadtime" : "orderLeadtime",
    "order_limit_time" : "orderLimitTime",
    "order_timing" : "orderTiming",
    "monthly_order_limit_day" : "monthlyOrderLimitDay",
    "monthly_order_target" : "monthlyOrderTarget",
    "enable_supplier_shipday" : "enableSupplierShipday",
    "enable_shipday_mon" : "enableShipdayMon",
    "enable_shipday_tue" : "enableShipdayTue",
    "enable_shipday_wed" : "enableShipdayWed",
    "enable_shipday_thu" : "enableShipdayThu",
    "enable_shipday_fri" : "enableShipdayFri",
    "enable_shipday_sat" : "enableShipdaySat",
    "enable_shipday_sun" : "enableShipdaySun",
    "enable_orderday_mon" : "enableOrderdayMon",
    "enable_orderday_tue" : "enableOrderdayTue",
    "enable_orderday_wed" : "enableOrderdayWed",
    "enable_orderday_thu" : "enableOrderdayThu",
    "enable_orderday_fri" : "enableOrderdayFri",
    "enable_orderday_sat" : "enableOrderdaySat",
    "enable_orderday_sun" : "enableOrderdaySun",
    "remarks": 'remarks',
    "delete_flg" : "deleteFlg"
};
const primayKey = ['orgSupplierId']

interface DataListProps extends DataListPropsBase {
  list: Supplier
  systype: Groups[]
  center: MstCenter[]
  searchParam: GetMstSuppliersSupplierGetRequest
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

interface MstSupplierWithId extends MstSupplier, RecordEditEx {}

function DataList({ 
  list, 
  isLoading, 
  systype,
  center,
  searchParam, 
  onReload, 
  updateTrigger, 
  onUploadData,
  setHasGridEdited,
  setResultMessage,
  setDownloadColumnDefs,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
}: DataListProps) {
  const { loginUserInfo, typeCode } = useAuthContext()
  const {result,isUpdating, fetchSupplierUpdate, supplierUpdateResult, supplierSearchResult} = useSupplier()
  const internalGridRef = useRef<AgGridReact<MstSupplierWithId>>(null)
  const gridRef = externalGridRef || internalGridRef
  const [orderSysTypeCodeLists, setOrderSysTypeCodeLists] = useState<{ [key: string]: Codes[] }>({})
  const [orderSysTypeCodeSelects, setOrderSysTypeCodeSelects] = useState<{ [key: string]: (string | null)[] }>({})
  // transport_org_center_id is not a type group, it's handled separately via center prop
  const orderUnitTypeGroups = ['order_timing', 'day_list', 'monthly_order_target']

  const tableFieldToGroup: { [key: string]: string } = {
    orderTiming: 'order_timing',
    monthlyOrderLimitDay: 'day_list',
    monthlyOrderTarget: 'monthly_order_target',
    enableSupplierShipday: 'day_list',
  }
  const defaultOrderTiming = typeCode.find((r) => r.typeGroup === 'order_timing')?.codes.find((r) => r.defaultFlg)?.typeCode || null
  const defaultMonthlyOrderTarget = typeCode.find((r) => r.typeGroup === 'monthly_order_target')?.codes.find((r) => r.defaultFlg)?.typeCode || null
  const defaultDayList = typeCode.find((r) => r.typeGroup === 'day_list')?.codes.filter((r) => r.defaultFlg).map((r) => r.typeCode) || []

  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${120 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  
  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${120 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  const [editorDataReady, setEditorDataReady] = useState(false);
  const [row, setRow] = useState<MstSupplierWithId[]>(
    list.list.map((item) => ({ ...item, ...initRecordEditEx() }))
  )
  
  // Use refs để lưu latest values tránh stale closure
  const centerRef = useRef(center)
  const rowRef = useRef(row)
  
  useEffect(() => {
    centerRef.current = center
    rowRef.current = row
  }, [center, row])
  useEffect(() => {
    setResultMessage(result)
  }, [result])
  useEffect(() => {
    setEditorDataReady(false)
  }, [row]);

  useEffect(() => {
    const newLists: { [key: string]: Codes[] } = {}
    const newSelects: { [key: string]: (string | null)[] } = {}

    orderUnitTypeGroups.forEach((group) => {
      const foundGroup = typeCode?.find((r) => r.typeGroup === group)
      const codes = foundGroup?.codes || []
      newLists[group] = codes
      newSelects[group] = codes.map((r) => r.typeCode)
    })

    setOrderSysTypeCodeLists(newLists)
    setOrderSysTypeCodeSelects(newSelects)
  }, [typeCode])

  useEffect(() => {
    if (center.length > 0) {
      setEditorDataReady(true)
    } else {
      setEditorDataReady(false)
    }
  }, [center])

  const createBlankRow = useCallback((): MstSupplierWithId => ({
      companyId: loginUserInfo?.companyId || null,
      orgSupplierId: null,
      supplierCompanyId: null,
      supplierName: null,
      supplierHojinNo: null,
      supplierAddress: null,
      supplierGln: null,
      minOrderQuantityOnce: 1,
      orderLeadtime: 2,
      orderLimitTime: null,
      orderTiming: defaultOrderTiming,
      monthlyOrderLimitDay: defaultDayList[0] || null,
      monthlyOrderTarget: defaultMonthlyOrderTarget,
      enableSupplierShipday: defaultDayList,
      enableShipdayMon: true,
      enableShipdayTue: true,
      enableShipdayWed: true,
      enableShipdayThu: true,
      enableShipdayFri: true,
      enableShipdaySat: false,
      enableShipdaySun: false,
      enableOrderdayMon: true,
      enableOrderdayTue: true,
      enableOrderdayWed: true,
      enableOrderdayThu: true,
      enableOrderdayFri: true,
      enableOrderdaySat: false,
      enableOrderdaySun: false,
      remarks: null,
      deleteFlg: false,
      insertDatetime: null,
      updateDatetime: null,
      insertUser: loginUserInfo?.userId || null,
      deleteDatetime: null,
      deleteUser: null,
      updateUser: null,
      transportOrgCenterId: null,
      ...initRecordEditEx(true)
    } as MstSupplierWithId
  ), [])

  // 入力チェック
  const fieldValidate = (pRow: MstSupplierWithId, field: string | undefined, newValue: any): boolean => {
    let hasErrors = false
    switch (field){
      case 'orgCenterId' :
      case "orgSupplierId": {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue));
        if (!hasErrors) {
          hasErrors = duplicateCheck(pRow, newValue)
        }
        break;
      }

      case "supplierName":
      case "minOrderQuantityOnce":
      case "orderLeadtime":
      case "orderTiming":
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue));
        break;

      case "transportOrgCenterId": {
        const validCenter = centerRef.current.find(c => c.orgCenterId === newValue)
        if (!validCenter && newValue) {
          hasErrors = checkAndSetError(pRow, field, "移送元自社センターIDが存在しないか、移送拠点センターではありません");
        }
        if (newValue && validCenter) {
          // 同一コードは他の仕入先に設定できない
          // gridApiから最新のデータを取得して重複チェック
          let duplicateFound = false
          gridRef.current?.api?.forEachNode((node) => {
            if (node.data && node.data.id !== pRow.id && node.data.transportOrgCenterId === newValue) {
              duplicateFound = true
            }
          })
          if (duplicateFound) {
            hasErrors = checkAndSetError(pRow, field, "同一コードは他の仕入先に設定できません");
          }
        }
        break;
      }

      default:
        return false;
    }
    return hasErrors

  };

  
  const cellValueChanged = (event: CellValueChangedEvent<MstSupplierWithId>, hasError: boolean) => {
    const { data, colDef, newValue, oldValue, node } = event
    const { field } = colDef

    const updatedRow = { ...data }
    // 値の変更時に、APIのエラーを解消
    const fieldAny = field as any;
    switch (fieldAny){
      case "orgSupplierId":
      case "supplierName":
        checkAndSetError(updatedRow, "orgSupplierId", null);
        fieldValidate(updatedRow, "orgSupplierId", updatedRow.orgSupplierId);
        break;
      case "orderTiming": {
        const rowAny = updatedRow as any;
        checkAndSetError(updatedRow, "orderTiming" as any, null);
        fieldValidate(updatedRow, "orderTiming", rowAny.orderTiming);
        break;
      }
      case "transportOrgCenterId": {
        checkAndSetError(updatedRow, "transportOrgCenterId", null);
        fieldValidate(updatedRow, "transportOrgCenterId", updatedRow.transportOrgCenterId);
        
        // 他の行のtransportOrgCenterIdも再検証して、重複エラーをクリア/設定
        // すべての行を再検証（新しい値と同じ行、古い値と同じ行の両方）
        const gridApi = gridRef.current?.api
        if (gridApi && !gridApi.isDestroyed()) {
          const rowsToUpdate: MstSupplierWithId[] = []
          gridApi.forEachNode((rowNode) => {
            if (rowNode.data && rowNode.data.id !== updatedRow.id && rowNode.data.transportOrgCenterId) {
              const otherRow = rowNode.data
              // 現在のエラーをクリア
              checkAndSetError(otherRow, "transportOrgCenterId", null)
              // 再検証
              fieldValidate(otherRow, "transportOrgCenterId", otherRow.transportOrgCenterId)
              rowsToUpdate.push(otherRow)
            }
          })
          if (rowsToUpdate.length > 0) {
            gridApi.applyTransaction({ update: rowsToUpdate })
          }
        }
        break;
      }
      default:
    }
  };

  const updateMstSupplier = useCallback(
    async (updatedRows: MstSupplierWithId[]) => {
      const datalist = updatedRows.map(({ isEdited, orgSupplierId, supplierName, editedFields, errors, ...rest }) => ({
        ...rest,
        orgSupplierId: orgSupplierId ??  '',
        supplierName: supplierName ?? ''
      }));
      
      try {
        await fetchSupplierUpdate({
          mstSupplierUpate: { body: datalist }
        })
        if (onReload) onReload()
      } catch (err: any) {
        if (err?.errors && err.result?.status === "error") {
          const gridApi = gridRef.current?.api
          err.errors.forEach((errrow: any) => {
            const {rowdata}=errrow
            const orgSupplierId = (rowdata as { org_supplier_id : string | null })?.org_supplier_id;
            const errRow = updatedRows.find((rowdatas) => rowdatas.orgSupplierId === orgSupplierId)
            if (errRow) {
              // UpdateAPIのErrorは、PKをエラー表記して、サーバーから取得したエラーを表示する
              errRow.errors = { orgSupplierId: [errrow.errorMessage] }
              const rowNode = gridApi?.getRowNode(errRow.id || '')
              if (rowNode) {
                rowNode.setData(errRow)
              }
            }
          });
        }
      }
    }, [fetchSupplierUpdate, searchParam]);

  const handleImportExcel = (data: any[]) => {
    let hasError = false;
    const mappedData = data.map((excelRow: any) => {
      const mappedRow: Partial<MstSupplierWithId> = {
      };
      Object.keys(columnMapping).forEach((key) => {
        if (key in excelRow) {
          const tableField = columnMapping[key as keyof typeof columnMapping];
          const importvalue = excelRow[key];
          let setvalue = importvalue || null;
          if (['deleteFlg', 'enableShipdayMon', 'enableShipdayTue', 'enableShipdayWed', 'enableShipdayThu', 
            'enableShipdayFri', 'enableShipdaySat', 'enableShipdaySun', 'enableOrderdayMon', 'enableOrderdayTue', 
            'enableOrderdayWed', 'enableOrderdayThu', 'enableOrderdayFri', 'enableOrderdaySat', 'enableOrderdaySun'].includes(tableField)) {
            setvalue = parseBoolean(importvalue)

          } else if (['minOrderQuantityOnce', 'orderLeadtime'].includes(tableField)) {
            setvalue = parsePositiveIntegerZero(importvalue);
          
          } else if (['orderLimitTime'].includes(tableField)) {
            setvalue = parseTime(importvalue);
          } else if (
            [
              'orderTiming',
              'monthlyOrderLimitDay',
              'monthlyOrderTarget',
            ].includes(tableField)
          ) {
            const group = tableFieldToGroup[tableField]
            const typeList = orderSysTypeCodeLists[group]

            setvalue = typeCodeParser(importvalue, typeList)
            if (!setvalue && importvalue) hasError = true
          } else if (['enableSupplierShipday'].includes(tableField)) {
            const group = tableFieldToGroup[tableField]
            const typeList = orderSysTypeCodeLists[group]

            if (typeList && typeList.length > 0 && importvalue) {
              // split by comma and parse each value
              const stringValue = String(importvalue).trim()
              const values = stringValue.split(',').map(v => v.trim()).filter(v => v)
              const parsedValues: string[] = []
              
              values.forEach(val => {
                const codevalue = typeCodeParser(val, typeList)
                if (codevalue) {
                  parsedValues.push(codevalue)
                } else {
                  hasError = true
                }
              })
              
              setvalue = parsedValues.length > 0 ? parsedValues : []
            } else {
              setvalue = []
            }
          } else if (['transportOrgCenterId'].includes(tableField)) {
            const validCenter = centerRef.current.find(c => c.orgCenterId === importvalue)
            if (validCenter && validCenter.internalTransferHubFlg === true) {
              setvalue = importvalue
            } else if (importvalue) {
              hasError = true
            }
          } else {
            setvalue = importvalue ? String(importvalue).trim() : null
          }
          // パース出来ないときはスキップ（＝NULL）し、警告メッセージを表示
          if (!isNonInputValue(importvalue) && setvalue === null) hasError = true
          mappedRow[tableField as keyof MstSupplierWithId] = setvalue as unknown as undefined
        }
      });
      return mappedRow as MstSupplierWithId;
    });

    // Validate duplicate transportOrgCenterId after parsing all data
    const transportOrgCenterIds = new Set<string>()
    mappedData.forEach((record, index) => {
      if (record.transportOrgCenterId) {
        if (transportOrgCenterIds.has(record.transportOrgCenterId)) {
          hasError = true
        } else {
          transportOrgCenterIds.add(record.transportOrgCenterId)
        }
      }
    })

    return { mappedData, hasError }
  };

  const {
    booleanFormatter,
    dateFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    numberValueParserZero,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parsePositiveIntegerZero,
    parseStringValue,
    parseTime,
    timeParser,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    multiSelectTypecodeFormatter,
    multiSelectTypecodeParser,
    multiSelectTypecodeFilterValueGetter,
    multiSelectTypecodeCellEditorParams,
    localeText,
    autoSizeStrategy,
    onGridReady,
    getCellClassRules,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    isEnableEdit,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    editInfoValueGetter,
    makeWeekDayChiled,
    clearPinnedRows,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    onReload,
    onUploadData,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    columnMapping,
    primayKey,
    storageKey: 'gridColumnState:/master-supplier',
    updateData: updateMstSupplier,
    handleImportExcel,
    createBlankRow,
    fieldValidate,
    cellValueChanged,
    scrollPositionKey: 'orgSupplierId',
    rowData: row,
  })

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef | ColGroupDef )[] => 
  [
    {
      headerName: '!',
      rowDrag: true,
      cellRenderer: EditStatusCellRenderer,
      valueGetter: (params) => editInfoValueGetter(params),
      pinned: 'left',
      width: 100,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
    },
    {
      headerName: '基本情報',
      children: [
        { 
          headerName: '仕入先ID',
          field: 'orgSupplierId',
          editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
          cellClassRules: {
            ...getCellClassRules(),
            'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
          },
          pinned: 'left',
          width: 200,
        },
        { 
          headerName: 'PSI仕入先ID',
          field: 'supplierCompanyId',
          width: 200,
        }, 
        { 
          headerName: '仕入先名',
          field: 'supplierName',
          editable: isEnableEdit(),
          cellClassRules: getCellClassRules(),
          pinned: 'left',
          width: 200,
        }, 
        {
          headerName: '移送元自社センターID',
          field: 'transportOrgCenterId',
          width: 200,
          editable: isEnableEdit(),
          cellClassRules: getCellClassRules(),
          cellEditor: 'agRichSelectCellEditor',
          valueFormatter: (params: ValueFormatterParams) =>
            selectValueFormatter(params.value, center, 'orgCenterId', 'centerName'),
          valueParser: (params: ValueParserParams) =>
            selectValueParser(params, center, 'orgCenterId'),
          filter: 'agMultiColumnFilter',
          filterParams: {
            valueFormatter: (params: ValueFormatterParams) =>
              selectValueFormatter(params.value, center, 'orgCenterId', 'centerName'),
          },
          cellEditorParams: editorDataReady
            ? ({
                values: [null, ...center.map(c => c.orgCenterId)],
                formatValue: (value: string) =>
                  selectValueFormatter(value, center, 'orgCenterId', 'centerName'),
                searchType: 'matchAny',
                allowTyping: true,
                filterList: true,
                highlightMatch: true,
              } as IRichCellEditorParams)
            : { values: ['-'] },
        },
        { 
          headerName: '会社法人等番号（登記所）',
          field: 'supplierHojinNo',
          width: 200,
          editable: isEnableEdit(),
          cellClassRules: getCellClassRules(),
        },
        
        { 
          headerName: '本社住所',
          field: 'supplierAddress',
          width: 200,
          editable: isEnableEdit(),
          cellClassRules: getCellClassRules(),

        }, 
        { 
          headerName: 'GS1事業者コード',
          field: 'supplierGln',
          width: 200,
          editable: isEnableEdit(),
          cellClassRules: getCellClassRules(),
        }
      ],
      
    },
    {
      headerName: '発注条件',
        children: [
          { 
            headerName: '1回あたり最低発注数',
            field: 'minOrderQuantityOnce',
            width: 200,
            editable: isEnableEdit(),
            cellClass:'number-cell',
            cellEditor:'agNumberCellEditor',
            // cellEditorParams: {
            //   step: 1,
            //   min: 0, 
            // },
            valueParser: numberValueParserZero,
            valueFormatter: numberValueFormatter,
          },
          {
            headerName: '発注タイミング',
            field: 'orderTiming',
            width: 100,
            editable: isEnableEdit(),
            cellClassRules: getCellClassRules(),
            cellEditor: 'agRichSelectCellEditor',
            valueFormatter: (params: ValueFormatterParams) =>
              typecodeFormatter(params.value, orderSysTypeCodeLists['order_timing'!]),
            valueParser: (params: ValueParserParams) =>
              selectTypecodeValueParser(params, orderSysTypeCodeLists['order_timing'!]),
            filterValueGetter: (params) => typecodeFormatter(params.data.orderTiming, orderSysTypeCodeLists['order_timing'!]),
            cellEditorParams: ({
                  values: [null, ...(orderSysTypeCodeSelects['order_timing'!] || [])],
                  formatValue: (value: string) =>
                    selectValueFormatter(value, orderSysTypeCodeLists['order_timing'!] || [], 'typeCode', 'typeName'),
                } as IRichCellEditorParams),
          },
          { 
            headerName: '締め時間',
            field: 'orderLimitTime',
            width: 200,
            editable: isEnableEdit(),
            cellClass:'number-cell',
            cellEditor:'agTextCellEditor',
            valueFormatter: (params) => params.value || 'hh:mm',
            valueParser: timeParser,
            cellClassRules: {
              'time-placeholder': (params) => {
                const editingCells = params.api?.getEditingCells?.() || []
                const isEditing = editingCells.some(
                  (cell) =>
                    cell.rowIndex === params.node.rowIndex &&
                    cell.column?.getColId() === params.column?.getColId()
                )
                return !params.value && !isEditing
              },
            },
          }
        ]
    },
    {
        headerName: '随時発注',
        children: [
          {
            headerName: '発注リードタイム',
            field: 'orderLeadtime',
            width: 200,
            editable: isEnableEdit(),
            cellClass: 'number-cell',
            cellEditor: 'agNumberCellEditor',
            // cellEditorParams: {
            //   step: 1,
            //   min: 0, 
            // },
            valueParser: numberValueParserZero,
            valueFormatter: numberValueFormatter,
          }
        ]
    },
    {
      headerName: '月次発注',
        children: [
          {
            headerName: '発注締日',
            field: 'monthlyOrderLimitDay',
            width: 100,
            editable: isEnableEdit(),
            cellClassRules: getCellClassRules(),
            cellEditor: 'agRichSelectCellEditor',
            valueFormatter: (params: ValueFormatterParams) =>
              typecodeFormatter(params.value, orderSysTypeCodeLists['day_list'!]),
            valueParser: (params: ValueParserParams) =>
              selectTypecodeValueParser(params, orderSysTypeCodeLists['day_list'!]),
            filterValueGetter: (params) => typecodeFormatter(params.data.monthlyOrderLimitDay, orderSysTypeCodeLists['day_list'!]),
            cellEditorParams: ({
              values: [null, ...(orderSysTypeCodeSelects['day_list'!] || [])],
              formatValue: (value: string) =>
                selectValueFormatter(value, orderSysTypeCodeLists['day_list'!] || [], 'typeCode', 'typeName'),
            } as IRichCellEditorParams),
          },
          {
            headerName: '入荷期間',
            field: 'monthlyOrderTarget',
            width: 100,
            editable: isEnableEdit(),
            cellClassRules: getCellClassRules(),
            cellEditor: 'agRichSelectCellEditor',
            valueFormatter: (params: ValueFormatterParams) =>
              typecodeFormatter(params.value, orderSysTypeCodeLists['monthly_order_target'!]),
            valueParser: (params: ValueParserParams) =>
              selectTypecodeValueParser(params, orderSysTypeCodeLists['monthly_order_target'!]),
            filterValueGetter: (params) => typecodeFormatter(params.data.monthlyOrderTarget, orderSysTypeCodeLists['monthly_order_target'!]),
            cellEditorParams: ({
                  values: [null, ...(orderSysTypeCodeSelects['monthly_order_target'!] || [])],
                  formatValue: (value: string) =>
                    selectValueFormatter(value, orderSysTypeCodeLists['monthly_order_target'!] || [], 'typeCode', 'typeName'),
                } as IRichCellEditorParams),
          },
        ]
    },
    {
      headerName: '仕入先の納入日設定',
        children: [
          {
            headerName: '納入可能日',
            field: 'enableSupplierShipday',
            width: 200,
            editable: isEnableEdit(),
            cellClassRules: getCellClassRules(),
            cellEditor: 'agRichSelectCellEditor',
            valueFormatter: (params: ValueFormatterParams) =>
              multiSelectTypecodeFormatter(params, orderSysTypeCodeLists['day_list'!] || []),
            valueParser: (params: ValueParserParams) =>
              multiSelectTypecodeParser(params, orderSysTypeCodeLists['day_list'!] || []),
            filterValueGetter: (params) =>
              multiSelectTypecodeFilterValueGetter(params, 'enableSupplierShipday', orderSysTypeCodeLists['day_list'!] || []),
            cellEditorParams: multiSelectTypecodeCellEditorParams(
              true,
              orderSysTypeCodeLists['day_list'!] || [],
              orderSysTypeCodeSelects['day_list'!] || []
            ),
          },
        ]
    },
    {
      headerName: '仕入先の納入可能曜日',
      children: makeWeekDayChiled('enableShipday', {
        editable: isEnableEdit(),
        width: 70,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        valueFormatter: formatBooleanValue,
        valueParser: parseBooleanValue,
      }),
    },
    {
      headerName: '受注受付可能曜日',
      children: makeWeekDayChiled('enableOrderday', {
        editable: isEnableEdit(),
        width: 70,
        cellRenderer: 'agCheckboxCellRenderer',
        cellEditor: 'agCheckboxCellEditor',
        valueFormatter: formatBooleanValue,
        valueParser: parseBooleanValue,
      }),
    },
    {
      headerName: '備考',
      field: 'remarks', 
      width: 200,
      editable: isEnableEdit(),
      cellEditorPopup: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: longTextEditorParams,
      valueFormatter: longTextValueFormatter,
      valueParser: longTextValueParser,
      valueSetter: longTextValueSetter,
    },
    {
      headerName: '削除',
      field: 'deleteFlg', // 削除
      width: 200,
      editable: isEnableEdit(),
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueFormatter: formatBooleanValue,
      valueParser: parseBooleanValue, 
    },
  ];

  const columnDefs = useMemo<(ColDef)[]>(updateColumnDefs, [editorDataReady, orderSysTypeCodeLists, orderSysTypeCodeSelects])

  // columnDefsが再計算された際に、保存済みのカラム表示設定を再適用する
  useEffect(() => {
    const api = gridRef.current?.api
    if (!api || api.isDestroyed()) return
    try {
      const savedAll = localStorage.getItem('gridColumnStates')
      if (savedAll) {
        const obj = JSON.parse(savedAll) as Record<string, any>
        const state = obj?.['gridColumnState:/master-supplier']
        if (Array.isArray(state) && state.length > 0) {
          // requestAnimationFrameでAG GridがcolumnDefsを処理した後に適用
          requestAnimationFrame(() => {
            if (!api.isDestroyed()) {
              api.applyColumnState({ state, applyOrder: true })
            }
          })
        }
      }
    } catch { /* noop */ }
  }, [columnDefs])

  // ダウンロードファイル用のカラム定義
  useEffect(() => {
    if (setDownloadColumnDefs)
      setDownloadColumnDefs([
        { headerName: '仕入先ID', children: [{ headerName: 'org_supplier_id', field: 'orgSupplierId', cellClass: 'stringType' }] },
        { headerName: 'PSI仕入先ID', children: [{ headerName: 'supplier_company_id', field: 'supplierCompanyId', cellClass: 'stringType' }] },
        { headerName: '仕入先名', children: [{ headerName: 'supplier_name', field: 'supplierName', cellClass: 'stringType' }] },
        { headerName: '会社法人等番号（登記所）', children: [{ headerName: 'supplier_hojin_no', field: 'supplierHojinNo', cellClass: 'stringType' }] },
        { headerName: '本社住所', children: [{ headerName: 'supplier_address', field: 'supplierAddress', cellClass: 'stringType' }] },
        { headerName: 'GS1事業者コード', children: [{ headerName: 'supplier_gln', field: 'supplierGln', cellClass: 'stringType' }] },
        { headerName: '移送元自社センターID', children: [{ headerName: 'transport_org_center_id', field: 'transportOrgCenterId', cellClass: 'stringType' }] },
        { headerName: '1回あたり最低発注数', children: [{ headerName: 'min_order_quantity_once', field: 'minOrderQuantityOnce', cellClass: 'numberType' }] },
        { headerName: '発注タイミング', children: [{ headerName: 'order_timing', field: 'orderTiming', cellClass: 'stringType' }] },
        { headerName: '発注リードタイム', children: [{ headerName: 'order_leadtime', field: 'orderLeadtime', cellClass: 'numberType' }] },
        { headerName: '締め時間', children: [{ headerName: 'order_limit_time', field: 'orderLimitTime', cellClass: 'stringType' }] },
        { headerName: '発注締日', children: [{ headerName: 'monthly_order_limit_day', field: 'monthlyOrderLimitDay', cellClass: 'stringType' }] },
        { headerName: '入荷期間', children: [{ headerName: 'monthly_order_target', field: 'monthlyOrderTarget', cellClass: 'stringType' }] },
        { headerName: '納入可能日', children: [{ headerName: 'enable_supplier_shipday', field: 'enableSupplierShipday', cellClass: 'stringType' }] },
        { headerName: '仕入先の納入可能曜日（月曜日）', children: [{ headerName: 'enable_shipday_mon', field: 'enableShipdayMon', cellClass: 'booleanType' }] },
        { headerName: '仕入先の納入可能曜日（火曜日）', children: [{ headerName: 'enable_shipday_tue', field: 'enableShipdayTue', cellClass: 'booleanType' }] },
        { headerName: '仕入先の納入可能曜日（水曜日）', children: [{ headerName: 'enable_shipday_wed', field: 'enableShipdayWed', cellClass: 'booleanType' }] },
        { headerName: '仕入先の納入可能曜日（木曜日）', children: [{ headerName: 'enable_shipday_thu', field: 'enableShipdayThu', cellClass: 'booleanType' }] },
        { headerName: '仕入先の納入可能曜日（金曜日）', children: [{ headerName: 'enable_shipday_fri', field: 'enableShipdayFri', cellClass: 'booleanType' }] },
        { headerName: '仕入先の納入可能曜日（土曜日）', children: [{ headerName: 'enable_shipday_sat', field: 'enableShipdaySat', cellClass: 'booleanType' }] },
        { headerName: '仕入先の納入可能曜日（日曜日）', children: [{ headerName: 'enable_shipday_sun', field: 'enableShipdaySun', cellClass: 'booleanType' }] },
        { headerName: '受注受付可能曜日（月曜日）', children: [{ headerName: 'enable_orderday_mon', field: 'enableOrderdayMon', cellClass: 'booleanType' }] },
        { headerName: '受注受付可能曜日（火曜日）', children: [{ headerName: 'enable_orderday_tue', field: 'enableOrderdayTue', cellClass: 'booleanType' }] },
        { headerName: '受注受付可能曜日（水曜日）', children: [{ headerName: 'enable_orderday_wed', field: 'enableOrderdayWed', cellClass: 'booleanType' }] },
        { headerName: '受注受付可能曜日（木曜日）', children: [{ headerName: 'enable_orderday_thu', field: 'enableOrderdayThu', cellClass: 'booleanType' }] },
        { headerName: '受注受付可能曜日（金曜日）', children: [{ headerName: 'enable_orderday_fri', field: 'enableOrderdayFri', cellClass: 'booleanType' }] },
        { headerName: '受注受付可能曜日（土曜日）', children: [{ headerName: 'enable_orderday_sat', field: 'enableOrderdaySat', cellClass: 'booleanType' }] },
        { headerName: '受注受付可能曜日（日曜日）', children: [{ headerName: 'enable_orderday_sun', field: 'enableOrderdaySun', cellClass: 'booleanType' }] },
        { headerName: '備考', children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }] },
        { headerName: '削除', children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }] },
      ])
  }, [setDownloadColumnDefs])

  useEffect(() => {
    if (list.list.length === 0) {
      const blankRow = createBlankRow();
      setRow([blankRow]);
    } else {
      setRow(list.list.map(item => {
        const itemAny = item as any
        return {
          ...item,
          ...initRecordEditEx(),
          orderTiming: itemAny.orderTiming,
          monthlyOrderLimitDay: (() => {
            const val = itemAny.monthlyOrderLimitDay;
            if (Array.isArray(val)) return val[0] ?? null;
            return val;
          })(),
          monthlyOrderTarget: itemAny.monthlyOrderTarget,
          enableSupplierShipday: (() => {
            const shipday = itemAny.enableSupplierShipday
            if (Array.isArray(shipday)) {
              return shipday
            }
            if (shipday) {
              return [shipday]
            }
            return []
          })(),
        }
      }))
      clearPinnedRows()
    }
  }, [list.list]);

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column', height: '69vh' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            rowData={row}
            ref={gridRef}
            getRowId={(params) => params.data.id}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            localeText={localeText}
            loadingOverlayComponent={LoadingOverlay}
            findSearchValue={externalFindSearchValue}
            onFindChanged={externalOnFindChanged}
            autoSizeStrategy={autoSizeStrategy}
            suppressColumnVirtualisation
            stopEditingWhenCellsLoseFocus
          />
        </div>
      </div>
    </div>
  );
}

export default DataList;
